import React from 'react'
import { useSignIn, UseSignInReturn } from '@features/account/contexts/signin/_signIn'

export interface SignInContextType {
  // Raw state for advanced consumers
  state: UseSignInReturn['state']

  // Derived helpers
  canGoNext: boolean
  canGoPrevious: boolean
  canSubmit: boolean
  isStepTouched: boolean

  // Mutations / actions
  updateField: ReturnType<typeof useSignIn>['updateField']
  nextStep: ReturnType<typeof useSignIn>['nextStep']
  previousStep: ReturnType<typeof useSignIn>['previousStep']
  setCurrentStep: ReturnType<typeof useSignIn>['setCurrentStep']
  submit: () => Promise<void>

  // Error handling
  setError: ReturnType<typeof useSignIn>['setError']
  clearError: ReturnType<typeof useSignIn>['clearError']
  setValidationErrors: ReturnType<typeof useSignIn>['setValidationErrors']
  setFieldError: ReturnType<typeof useSignIn>['setFieldError']
  clearFieldErrors: ReturnType<typeof useSignIn>['clearFieldErrors']
  resetContext: ReturnType<typeof useSignIn>['resetContext']

  // Recovery state/actions for help panel
  recoveryFormData: UseSignInReturn['recoveryFormData']
  updateRecoveryField: UseSignInReturn['updateRecoveryField']
  handleRecoveryInputValueChange: UseSignInReturn['handleRecoveryInputValueChange']
  recoveryCanSubmit: UseSignInReturn['recoveryCanSubmit']
  submitRecovery: UseSignInReturn['submitRecovery']
  recoveryLoading: UseSignInReturn['recoveryLoading']
  recoveryError: UseSignInReturn['recoveryError']

  // Enhanced recovery state
  recoveredUsername: UseSignInReturn['recoveredUsername']
  passwordResetEmail: UseSignInReturn['passwordResetEmail']
  newPassword: UseSignInReturn['newPassword']
  setNewPassword: UseSignInReturn['setNewPassword']
  confirmPassword: UseSignInReturn['confirmPassword']
  setConfirmPassword: UseSignInReturn['setConfirmPassword']
  accountLockoutTime: UseSignInReturn['accountLockoutTime']
  resendEmailCooldown: UseSignInReturn['resendEmailCooldown']

  // Enhanced recovery functions
  canResetPassword: UseSignInReturn['canResetPassword']
  isAccountLocked: UseSignInReturn['isAccountLocked']
  canResendEmail: UseSignInReturn['canResendEmail']
  getRemainingLockoutTime: UseSignInReturn['getRemainingLockoutTime']
  getRemainingResendTime: UseSignInReturn['getRemainingResendTime']
  submitPasswordResetEmail: UseSignInReturn['submitPasswordResetEmail']
  resendPasswordResetEmail: UseSignInReturn['resendPasswordResetEmail']
  submitNewPassword: UseSignInReturn['submitNewPassword']
  handleAccountLockout: UseSignInReturn['handleAccountLockout']
  copyUsernameToClipboard: UseSignInReturn['copyUsernameToClipboard']
  goBackToSignInWithUsername: UseSignInReturn['goBackToSignInWithUsername']

  // API execution for recovery operations
  executeRecoveryApi: UseSignInReturn['executeRecoveryApi']
}

export const SignInContext = React.createContext<SignInContextType | undefined>(undefined)
